import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Tag as TagIcon } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import { usePageHeader } from '@utils/hooks/use-page-header';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller';
import TagFormModal from '@components/tags/tag-form-modal.tsx';
import DeleteTagModal from '@components/tags/delete-tag-modal.tsx';
import OnboardingCallout from '@components/atoms/onboarding-callout.tsx';
import { useGetTags } from '@utils/api/organizations/tags';
import { Tag } from '@utils/types/organization/tag.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { Text } from '@components/atoms/ui-kit/text';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';

type Row = {
  id: number;
  name: string;
  color: string;
};

export default function Tags() {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  const offset = Number(searchParams.offset) || 0;
  const limit = Number(searchParams.limit) || 25;
  const q = (searchParams.q as string) || '';
  const sort = (searchParams.sort as string) || 'name';
  const direction = ((searchParams.direction as string) || 'asc') as
    | 'asc'
    | 'desc';

  const { data, isLoading } = useGetTags({
    offset,
    limit,
    q,
    sort,
    direction
  });

  const rows = useMemo<Row[]>(() => {
    if (!data?.result) return [];
    return data.result.map((tag: Tag) => ({
      id: tag.id,
      name: tag.name,
      color: tag.color || ''
    }));
  }, [data]);

  const handleEdit = (tag: Tag) => {
    setSelectedTag(tag);
    setIsEditModalOpen(true);
  };

  const handleDelete = (tag: Tag) => {
    setSelectedTag(tag);
    setIsDeleteModalOpen(true);
  };

  const actions = useMemo<GridAction<Row>[]>(() => {
    return [
      {
        id: 'edit',
        label: t('edit'),
        icon: <Edit className="h-3.5 w-3.5" />,
        run: row => {
          const tag = data?.result.find(s => s.id === row.id);
          if (tag) handleEdit(tag);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-3.5 w-3.5" />,
        intent: 'danger',
        run: row => {
          const tag = data?.result.find(s => s.id === row.id);
          if (tag) handleDelete(tag);
        }
      }
    ];
  }, [t, data]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        id: 'name',
        header: t('name'),
        field: 'name',
        minWidth: 240,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'name',
        serverOperator: 'ilike',
        cell: ({ row, getValue }) => (
          <Text
            as="span"
            size="sm"
            weight="medium"
            className="inline-flex items-center gap-2"
          >
            <span
              className="inline-block h-3 w-3 rounded-full shrink-0"
              style={{
                backgroundColor: row.original.color || 'var(--color-hairline)',
                border: '1px solid var(--color-hairline)'
              }}
            />
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'color',
        header: t('color'),
        field: 'color',
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const color = String(getValue() ?? '');
          if (!color) return '';
          return (
            <Text
              as="span"
              size="xs"
              tone="muted"
              className="inline-flex items-center gap-2"
            >
              <span
                className="inline-block h-3 w-3 rounded-full shrink-0"
                style={{
                  backgroundColor: color,
                  border: '1px solid var(--color-hairline)'
                }}
              />
              {color}
            </Text>
          );
        }
      }
    ];
  }, [t]);

  const showEmptyState = rows.length === 0 && !isLoading && !!data;

  usePageHeader({
    title: t('tags') || 'Tags',
    description:
      t('manage-tags-for-transcripts') ||
      'Manage tags used for named highlights in transcripts.',
    actions: (
      <Button
        onClick={() => setIsCreateModalOpen(true)}
        label={t('add-tag') || 'Add tag'}
        icon={<Plus width={14} />}
      />
    )
  });

  return (
    <div className="bg-app flex flex-col h-full min-h-0">
      <TagFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        tag={null}
      />

      <TagFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTag(null);
        }}
        tag={selectedTag}
      />

      <DeleteTagModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedTag(null);
        }}
        tag={selectedTag}
        onDeleteSuccess={() => setSelectedTag(null)}
      />

      <div className="px-4 sm:px-0 pt-3">
        <OnboardingCallout
          storageKey="onboarding.tags-page.dismissed"
          title={t('tags-onboarding-title')}
          steps={[
            t('tags-onboarding-step-1'),
            t('tags-onboarding-step-2'),
            t('tags-onboarding-step-3')
          ]}
        />
      </div>

      <div className="px-4 sm:px-0 py-4 sm:pb-0 relative flex-1 min-h-0 flex flex-col">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination={true}
          enableSorting={true}
          enableFiltering={true}
          showColumnFilters={false}
          filterMode="popover"
          fill
          dense
          grid={false}
          striped
          loading={isLoading}
          getRowId={row => String(row.id)}
          totalItems={data?.total_results ?? 0}
          manualPagination
          manualSorting
          manualFiltering
        />

        {showEmptyState && !q && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <EmptyState
                icon={<TagIcon className="h-7 w-7" />}
                title={t('no-tags-yet')}
                action={
                  <Button
                    label={t('add-first-tag')}
                    icon={<Plus width={14} />}
                    onClick={() => setIsCreateModalOpen(true)}
                    variant="primary"
                    size="small"
                  />
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
